import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Users,
  Calendar,
  CreditCard,
  Bell,
  TrendingUp,
  Clock,
  CheckCircle,
  FileText,
  Loader2,
  MessageSquare,
  AlertTriangle,
  AlertCircle,
  ChevronRight,
  Trophy,
  MapPin,
  RefreshCw,
  BookOpen,
  Upload,
  History,
  Plus,
  DollarSign,
  Shield,
  Shirt,
  X,
  PiggyBank,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SecondParentCard } from "@/components/SecondParentCard";
import { ChildPhotoAvatar } from "@/components/ChildPhotoAvatar";
import { SessionPhotosCard } from "@/components/SessionPhotosCard";
import { ParentFixturesCard, ParentAvailabilityCard } from "@/components/ParentFixturesAndAvailability";
import { PaymentProofUpload } from "@/components/PaymentProofUpload";
import { AuthImage } from "@/components/AuthImage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ParentDashboard({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [childCredits, setChildCredits] = useState({});
  
  // Credit history modal
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  const [selectedChildForCredits, setSelectedChildForCredits] = useState(null);
  const [creditHistory, setCreditHistory] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  
  // MC Upload modal
  const [showMCUpload, setShowMCUpload] = useState(false);
  const [selectedChildForMC, setSelectedChildForMC] = useState(null);
  const [mcFile, setMcFile] = useState(null);
  const [mcDate, setMcDate] = useState("");
  const [mcReason, setMcReason] = useState("");
  const [uploadingMC, setUploadingMC] = useState(false);
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState("");
  
  // Ad-hoc booking
  const [showAdhocBooking, setShowAdhocBooking] = useState(false);
  const [adhocSessions, setAdhocSessions] = useState([]);
  const [adhocPricing, setAdhocPricing] = useState(null);
  const [adhocCart, setAdhocCart] = useState([]);
  const [selectedChildForAdhoc, setSelectedChildForAdhoc] = useState(null);
  const [bookingAdhoc, setBookingAdhoc] = useState(false);
  const [loadingAdhoc, setLoadingAdhoc] = useState(false);

  // Membership card upload
  const [showMembershipUpload, setShowMembershipUpload] = useState(false);
  const [membershipFile, setMembershipFile] = useState(null);
  const [uploadingMembership, setUploadingMembership] = useState(false);
  const [membershipCardStatus, setMembershipCardStatus] = useState(null);

  // Pending payments
  const [pendingPayments, setPendingPayments] = useState([]);

  // Payment credits (overpayment balance from Command Centre)
  const [paymentCredits, setPaymentCredits] = useState(null);
  // Ad-hoc Digital Membership status (for the Upgrade CTA)
  const [dmStatus, setDmStatus] = useState(null);
  const [purchasingDm, setPurchasingDm] = useState(false);
  // Feb 2026: parent-facing "resume drafts" callout. Populated by
  // GET /registrations/mine/pending (no term_id → all pending regs).
  const [pendingDrafts, setPendingDrafts] = useState([]);

  useEffect(() => {
    fetchDashboard();
    fetchTerms();
    fetchMembershipCardStatus();
    fetchPendingPayments();
    fetchPaymentCredits();
    fetchDmStatus();
    fetchPendingDrafts();
  }, []);

  const fetchPendingDrafts = async () => {
    try {
      const res = await axios.get(`${API}/academy/registrations/mine/pending`, { withCredentials: true });
      setPendingDrafts(res.data?.registrations || []);
    } catch (err) {
      // 401 = not logged in yet; silent fail otherwise.
      if (err.response?.status !== 401) {
        console.debug("pending drafts fetch failed:", err.response?.status);
      }
      setPendingDrafts([]);
    }
  };

  // Fetch credits for all children after dashboard loads
  useEffect(() => {
    if (dashboardData?.children?.length > 0) {
      fetchAllChildCredits(dashboardData.children);
    }
  }, [dashboardData?.children]);

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/academy/terms`, { withCredentials: true });
      const activeTerms = (response.data || []).filter(t => t.is_finalized);
      setTerms(activeTerms);
      if (activeTerms.length > 0) {
        setSelectedTerm(activeTerms[0].id);
      }
    } catch (err) {
      console.error("Error fetching terms:", err);
    }
  };

  const fetchMembershipCardStatus = async () => {
    try {
      const response = await axios.get(`${API}/academy/membership-card/status`, { withCredentials: true });
      setMembershipCardStatus(response.data);
    } catch (err) {
      console.error("Error fetching membership card status:", err);
    }
  };

  const fetchPendingPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments/my-pending`, { withCredentials: true });
      setPendingPayments(response.data || []);
    } catch (err) {
      console.error("Error fetching pending payments:", err);
    }
  };

  const uploadMembershipCard = async () => {
    if (!membershipFile) {
      toast.error("Please select a file");
      return;
    }
    
    setUploadingMembership(true);
    try {
      const formData = new FormData();
      formData.append("file", membershipFile);
      
      await axios.post(`${API}/academy/membership-card/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Membership card uploaded! Admin will verify shortly.");
      setShowMembershipUpload(false);
      setMembershipFile(null);
      fetchMembershipCardStatus();
    } catch (err) {
      console.error("Error uploading membership card:", err);
      toast.error(err.response?.data?.detail || "Failed to upload membership card");
    } finally {
      setUploadingMembership(false);
    }
  };

  const fetchAdhocSessions = async () => {
    setLoadingAdhoc(true);
    try {
      const response = await axios.get(`${API}/academy/adhoc-sessions/available`, { withCredentials: true });
      setAdhocSessions(response.data.sessions || []);
      setAdhocPricing(response.data);
    } catch (err) {
      console.error("Error fetching ad-hoc sessions:", err);
      toast.error("Failed to load available sessions");
    } finally {
      setLoadingAdhoc(false);
    }
  };

  const openAdhocBooking = (child) => {
    setSelectedChildForAdhoc(child);
    setAdhocCart([]);
    setShowAdhocBooking(true);
    fetchAdhocSessions();
  };

  const toggleAdhocDate = (session, date) => {
    const key = `${session.id}__${date}`;
    const dayNames = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    setAdhocCart((prev) => {
      if (prev.some((i) => i.key === key)) return prev.filter((i) => i.key !== key);
      return [...prev, {
        key,
        session_id: session.id,
        session_name: session.name || `${dayNames[session.day_of_week]} Training`,
        day_of_week: session.day_of_week,
        start_time: session.start_time,
        end_time: session.end_time,
        date,
        price: session.your_adhoc_price || 0,
      }];
    });
  };

  const adhocCartTotal = adhocCart.reduce((sum, i) => sum + (i.price || 0), 0);

  const bookAdhocSessions = async () => {
    if (!selectedChildForAdhoc || adhocCart.length === 0) {
      toast.error("Please select at least one session date");
      return;
    }
    setBookingAdhoc(true);
    try {
      const res = await axios.post(`${API}/academy/adhoc-booking`, {
        player_id: selectedChildForAdhoc.id,
        booking_type: "single",
        line_items: adhocCart.map((i) => ({ session_id: i.session_id, specific_date: i.date })),
      }, { withCredentials: true });

      setShowAdhocBooking(false);
      const bookingId = res.data.booking_id;
      toast.success(`${adhocCart.length} session(s) booked! Proceed to payment.`);
      if (bookingId) {
        navigate(`/academy/payment/${bookingId}`);
      } else {
        fetchDashboard();
      }
    } catch (err) {
      console.error("Error booking ad-hoc sessions:", err);
      toast.error(err.response?.data?.detail || "Failed to book sessions");
    } finally {
      setBookingAdhoc(false);
    }
  };

  const fetchAllChildCredits = async (children) => {
    const credits = {};
    for (const child of children) {
      try {
        const response = await axios.get(`${API}/academy/parent/credits/${child.id}`, {
          withCredentials: true,
        });
        credits[child.id] = response.data;
      } catch (err) {
        // Silently fail - credits might not exist
        credits[child.id] = { balance: 0 };
      }
    }
    setChildCredits(credits);
  };

  const fetchCreditHistory = async (childId) => {
    setLoadingCredits(true);
    try {
      const response = await axios.get(`${API}/academy/parent/credits/${childId}/history`, {
        withCredentials: true,
      });
      setCreditHistory(response.data);
    } catch (err) {
      console.error("Error fetching credit history:", err);
      toast.error("Failed to load credit history");
    } finally {
      setLoadingCredits(false);
    }
  };

  const openCreditHistory = (child) => {
    setSelectedChildForCredits(child);
    setShowCreditHistory(true);
    fetchCreditHistory(child.id);
  };

  const openMCUpload = (child) => {
    setSelectedChildForMC(child);
    setShowMCUpload(true);
    setMcFile(null);
    setMcDate("");
    setMcReason("");
  };

  const uploadMC = async () => {
    if (!mcFile || !mcDate || !selectedTerm) {
      toast.error("Please fill in all required fields");
      return;
    }

    setUploadingMC(true);
    try {
      const formData = new FormData();
      formData.append("mc_file", mcFile);
      formData.append("term_id", selectedTerm);
      formData.append("mc_date", mcDate);
      formData.append("mc_reason", mcReason);

      await axios.post(
        `${API}/academy/parent/mc/upload/${selectedChildForMC.id}`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );

      toast.success("Medical Certificate submitted for review");
      setShowMCUpload(false);
      fetchAllChildCredits(dashboardData.children);
    } catch (err) {
      console.error("Error uploading MC:", err);
      toast.error(err.response?.data?.detail || "Failed to upload MC");
    } finally {
      setUploadingMC(false);
    }
  };

  const fetchPaymentCredits = async () => {
    try {
      const response = await axios.get(`${API}/academy/my-credits`, {
        withCredentials: true,
      });
      setPaymentCredits(response.data);
    } catch (err) {
      // Non-fatal — parent may not have the endpoint available in some rollouts
      console.warn("fetchPaymentCredits failed:", err?.response?.status);
    }
  };

  const fetchDmStatus = async () => {
    try {
      const response = await axios.get(`${API}/academy/me/dm-status`, {
        withCredentials: true,
      });
      setDmStatus(response.data);
    } catch (err) {
      console.warn("fetchDmStatus failed:", err?.response?.status);
    }
  };

  const purchaseDm = async () => {
    if (purchasingDm) return;  // Guard 1 — button-level debounce (prevents tap-twice)
    if (!dmStatus?.can_purchase) {
      toast.error("Digital Membership cannot be purchased right now.");
      return;
    }
    setPurchasingDm(true);
    try {
      const response = await axios.post(
        `${API}/academy/me/purchase-dm`,
        {},
        { withCredentials: true }
      );
      toast.success(`Invoice created · ref ${response.data.payment_reference}`);
      // Redirect to PayNow page to complete payment
      window.location.href = response.data.redirect_url;
    } catch (err) {
      // Guard 2 + 3 — backend 409 (already DM or in-flight)
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to create Digital Membership invoice");
      toast.error(msg);
      // Refresh state so the UI reflects the conflicting in-flight record
      fetchDmStatus();
    } finally {
      setPurchasingDm(false);
    }
  };

  const fetchDashboard = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await axios.get(`${API}/academy/parent/dashboard`, {
        withCredentials: true,
      });
      setDashboardData(response.data);
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      setError("Failed to load dashboard data");
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const markNotificationRead = async (notifId) => {
    try {
      await axios.post(
        `${API}/academy/notifications/${notifId}/read`,
        {},
        { withCredentials: true }
      );
      // Update local state
      setDashboardData((prev) => ({
        ...prev,
        notifications: {
          ...prev.notifications,
          items: prev.notifications.items.map((n) =>
            n.id === notifId ? { ...n, read: true } : n
          ),
          unread_count: Math.max(0, prev.notifications.unread_count - 1),
        },
      }));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await axios.post(
        `${API}/academy/notifications/mark-all-read`,
        {},
        { withCredentials: true }
      );
      // Update local state
      setDashboardData((prev) => ({
        ...prev,
        notifications: {
          ...prev.notifications,
          items: prev.notifications.items.map((n) => ({ ...n, read: true })),
          unread_count: 0,
        },
      }));
      toast.success("All notifications marked as read");
    } catch (err) {
      toast.error("Failed to mark notifications as read");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Unable to Load Dashboard</h3>
          <p className="text-slate-400 mb-4">{error || "Something went wrong"}</p>
          <Button onClick={() => fetchDashboard()}>Try Again</Button>
        </Card>
      </div>
    );
  }

  const { children, term, upcoming_sessions, recent_feedback, pending_payments, notifications, stats, savings_this_term, savings_term_name } = dashboardData;

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-6 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <Link to="/academy">
              <Button variant="ghost" className="text-white hover:bg-white/20">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Academy
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
              onClick={() => fetchDashboard(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
          <h1 className="text-2xl font-bold">Parent Dashboard</h1>
          <p className="text-purple-200">Welcome, {user?.name || user?.email}</p>
          {savings_this_term > 0 && (
            <div
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-400 border border-emerald-300 px-4 py-1.5 text-emerald-950 shadow-sm"
              data-testid="parent-savings-badge"
            >
              <PiggyBank className="w-4 h-4" />
              <span className="text-sm font-bold">
                You saved S${Number(savings_this_term).toFixed(2)}
                {savings_term_name ? ` this ${savings_term_name}` : " this term"}
              </span>
            </div>
          )}
          <div className="mt-3">
            <Link to="/handbook/parent" data-testid="parent-handbook-link">
              <Button size="sm" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                <BookOpen className="w-4 h-4 mr-2" />
                Parent Handbook
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Resume drafts callout (Feb 2026 duplicate-guard).
            Only rendered when the parent has one or more live pending
            registrations they haven't paid or cancelled yet. Clicking
            "Continue" jumps straight back into the wizard on the right
            term — no wasted second registration. */}
        {pendingDrafts.length > 0 && (
          <Card className="mb-6 bg-emerald-500/10 border-2 border-emerald-500/40" data-testid="pending-drafts-card">
            <CardContent className="pt-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-emerald-300 mb-1">
                    You have {pendingDrafts.length} unfinished registration{pendingDrafts.length > 1 ? "s" : ""}
                  </h3>
                  <p className="text-sm text-emerald-200/80 mb-3">
                    Pick up where you left off — your invoice number stays the same, no duplicates created.
                  </p>
                  <div className="space-y-2">
                    {pendingDrafts.map((d) => (
                      <Link
                        key={d.id}
                        to={`/academy/register?term_id=${d.term_id}`}
                        data-testid={`resume-draft-${d.id}`}
                        className="flex items-center justify-between gap-2 bg-slate-800/60 hover:bg-slate-800 border border-slate-700 hover:border-emerald-500/60 rounded-lg px-3 py-2 transition-colors group"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">
                            {d.term_name || "Term"}
                            {d.children_summary?.length > 0 && (
                              <span className="text-slate-400 font-normal">
                                {" "}— {d.children_summary.map(c => c.child_name).filter(Boolean).join(", ") || `${d.children_summary.length} child${d.children_summary.length > 1 ? "ren" : ""}`}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-400">
                            {d.payment_reference && (
                              <span className="font-mono">{d.payment_reference}</span>
                            )}
                            {d.updated_at && (
                              <span> • updated {new Date(d.updated_at).toLocaleDateString()}</span>
                            )}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-bold text-emerald-300">
                            ${Number(d.total_amount || 0).toFixed(2)}
                          </p>
                          <p className="text-[10px] text-slate-500 group-hover:text-emerald-400">
                            Resume →
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Payment Alert Banner */}
        {pendingPayments.length > 0 && (
          <Card className="mb-6 bg-amber-500/10 border-2 border-amber-500/30">
            <CardContent className="pt-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-amber-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-amber-400 mb-2">
                    ⏳ You Have {pendingPayments.length} Pending Payment{pendingPayments.length > 1 ? 's' : ''}
                  </h3>
                  {pendingPayments.map((payment, idx) => (
                    <div key={idx} className="mb-3 p-3 bg-slate-900/50 rounded-lg">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="text-sm text-white">
                            <strong>Ref:</strong> {payment.payment_reference} • 
                            <strong> Amount:</strong> ${payment.amount?.toFixed(2)} SGD
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            {payment.fee_breakdown?.children_count || 0} children enrolled
                          </p>
                          {payment.payment_reference && (
                            <PaymentProofUpload reference={payment.payment_reference} />
                          )}
                        </div>
                        <div className="text-right">
                          {payment.action_required === "confirm_payment" ? (
                            <Link to={`/academy/paynow-payment?ref=${payment.payment_reference}&amount=${payment.amount}&txn=${payment.id}`}>
                              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                                Complete Payment
                              </Button>
                            </Link>
                          ) : (
                            <span className="text-xs text-emerald-400 bg-emerald-500/20 px-2 py-1 rounded">
                              ✓ Awaiting Verification
                            </span>
                          )}
                          {payment.hours_remaining && payment.hours_remaining > 0 && (
                            <p className="text-xs text-amber-400 mt-1">
                              Expires in {Math.floor(payment.hours_remaining)}h {Math.round((payment.hours_remaining % 1) * 60)}m
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-900/50">
            <CardContent className="pt-4 text-center">
              <Users className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold">{stats?.total_children || 0}</p>
              <p className="text-sm text-slate-400">Children</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50">
            <CardContent className="pt-4 text-center">
              <Calendar className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold">{stats?.total_enrolled_sessions || 0}</p>
              <p className="text-sm text-slate-400">Sessions</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50">
            <CardContent className="pt-4 text-center">
              <Bell className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">{notifications?.unread_count || 0}</p>
              <p className="text-sm text-slate-400">Notifications</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50">
            <CardContent className="pt-4 text-center">
              <CreditCard className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-2xl font-bold">{stats?.total_pending_payments || 0}</p>
              <p className="text-sm text-slate-400">Pending</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Link to="/academy/session-upgrade">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20">
              <TrendingUp className="w-10 h-10 text-emerald-400 mb-3" />
              <h3 className="font-bold text-lg text-white">Add Sessions</h3>
              <p className="text-sm text-slate-300">Book new sessions or upgrade</p>
            </Card>
          </Link>
          <Link to="/academy/my-kids">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20">
              <Users className="w-10 h-10 text-purple-400 mb-3" />
              <h3 className="font-bold text-lg text-white">My Children</h3>
              <p className="text-sm text-slate-300">View profiles and stats</p>
            </Card>
          </Link>
          <Link to="/academy/start-registration">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20">
              <FileText className="w-10 h-10 text-blue-400 mb-3" />
              <h3 className="font-bold text-lg text-white">New Registration</h3>
              <p className="text-sm text-slate-300">Register another child</p>
            </Card>
          </Link>
        </div>

        {/* Membership Verification Card - Show if parent claims SCC member */}
        {user?.is_scc_member && (
          <Card className="mb-6 border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-slate-900">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <CreditCard className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">SCC Membership Verification</h3>
                    {membershipCardStatus?.has_card ? (
                      <p className="text-sm text-slate-400">
                        Status: {" "}
                        <span className={
                          membershipCardStatus.status === "verified" 
                            ? "text-emerald-400" 
                            : membershipCardStatus.status === "pending"
                              ? "text-amber-400"
                              : "text-red-400"
                        }>
                          {membershipCardStatus.status === "verified" ? "✓ Verified" : 
                           membershipCardStatus.status === "pending" ? "⏳ Pending Review" : 
                           "❌ " + membershipCardStatus.status}
                        </span>
                        {membershipCardStatus.claimed_membership_number && (
                          <span className="ml-2 text-slate-400">#{membershipCardStatus.claimed_membership_number}</span>
                        )}
                      </p>
                    ) : (
                      <p className="text-sm text-amber-400">
                        Upload your membership card to verify member discount eligibility
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => setShowMembershipUpload(true)}
                  size="sm"
                  className={membershipCardStatus?.status === "verified" 
                    ? "bg-emerald-600 hover:bg-emerald-700" 
                    : "bg-amber-600 hover:bg-amber-700"
                  }
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {membershipCardStatus?.has_card ? "Update Card" : "Upload Card"}
                </Button>
              </div>
              {membershipCardStatus?.card_id && (
                <div className="mt-3">
                  <AuthImage
                    src={`${API}/academy/membership-cards/${membershipCardStatus.card_id}/file`}
                    alt="Uploaded membership card"
                    className="max-h-32 rounded-lg border border-slate-700 object-contain bg-slate-950"
                    testId="membership-card-preview"
                    fallback={<p className="text-xs text-slate-500">Card on file (preview unavailable)</p>}
                  />
                </div>
              )}
              {membershipCardStatus?.status === "rejected" && membershipCardStatus?.review_notes && (
                <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-400">
                  Note: {membershipCardStatus.review_notes}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Children Overview */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" /> My Children
              </CardTitle>
            </CardHeader>
            <CardContent>
              {children?.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No children registered yet</p>
                  <Link to="/academy/start-registration">
                    <Button className="mt-3" size="sm">
                      Register a Child
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {children?.map((child) => (
                    <div
                      key={child.id}
                      className="flex items-center justify-between p-4 bg-[#0a0f1a] rounded-lg hover:bg-slate-800/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <ChildPhotoAvatar playerId={child.id} name={child.name} />
                        <div className="flex-1">
                          <p className="font-medium">{child.name}</p>
                          <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
                            <span>{child.enrolled_sessions_count || 0} sessions</span>
                            {child.membership_number && (
                              <span className="text-xs bg-slate-700 px-2 py-0.5 rounded">
                                {child.membership_number}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Ad-hoc Booking Button */}
                        <Button
                          onClick={() => openAdhocBooking(child)}
                          size="sm"
                          className="bg-orange-600 hover:bg-orange-500/100 text-xs"
                          data-testid={`adhoc-booking-${child.id}`}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Ad-hoc
                        </Button>
                        {/* Session Credits - Clickable */}
                        <button
                          onClick={() => openCreditHistory(child)}
                          className="text-center px-3 py-1 bg-emerald-500/20 rounded-lg hover:bg-emerald-500/100/30 transition-colors cursor-pointer"
                          data-testid={`view-credits-${child.id}`}
                        >
                          <p className="text-lg font-bold text-emerald-400">{childCredits[child.id]?.balance || 0}</p>
                          <p className="text-xs text-emerald-400/70">credits</p>
                        </button>
                        <div className="text-right text-sm">
                          <p className="flex items-center gap-1">
                            <Trophy className="w-3 h-3 text-yellow-500" />
                            {child.goals_scored || 0} goals
                          </p>
                        </div>
                        <Badge className="bg-purple-500/20 text-purple-400">
                          {child.age_group || "Unassigned"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Kit Orders — link to dedicated page (initial kit + replacement orders) */}
          <Link
            to="/academy/kit-orders"
            className="block group"
            data-testid="parent-kit-orders-tile"
          >
            <Card className="border-fuchsia-500/40 bg-gradient-to-r from-fuchsia-500/10 to-purple-500/10 hover:border-fuchsia-400 transition-colors cursor-pointer">
              <CardContent className="py-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-fuchsia-500/20 rounded-full">
                    <Shirt className="w-6 h-6 text-fuchsia-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white group-hover:text-fuchsia-200 transition-colors">Kit Orders</h3>
                    <p className="text-sm text-slate-400">Order your child&apos;s initial kit, request a replacement, or buy extras.</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-fuchsia-300 group-hover:translate-x-1 transition-all" />
              </CardContent>
            </Card>
          </Link>

          {/* Ad-hoc Digital Membership upgrade CTA (shown only if eligible) */}
          {dmStatus && dmStatus.can_purchase && (
            <Card className="border-blue-500/40 bg-blue-500/5" data-testid="dm-upgrade-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-blue-300">
                  <Shield className="w-5 h-5" /> Upgrade to Digital Member
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-300 mb-3">
                  Unlock member pricing on academy fees, priority booking, and access to SCC Football digital benefits.
                </p>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-3xl font-bold text-blue-300" data-testid="dm-price">
                    ${Number(dmStatus.price).toFixed(2)}
                  </span>
                  <span className="text-xs text-slate-400">SGD / year</span>
                </div>
                <Button
                  onClick={purchaseDm}
                  disabled={purchasingDm}
                  className="w-full bg-blue-600 hover:bg-blue-500"
                  data-testid="dm-upgrade-btn"
                >
                  {purchasingDm ? "Creating invoice…" : "Become a Digital Member"}
                </Button>
                <p className="mt-2 text-[11px] text-slate-500">
                  You'll receive a PayNow invoice. Membership activates once the payment is verified.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Ad-hoc DM: in-flight payment state (shown instead of CTA when pending) */}
          {dmStatus && dmStatus.has_in_flight && !dmStatus.is_digital_member && (
            <Card className="border-amber-500/40 bg-amber-500/5" data-testid="dm-in-flight-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-amber-300">
                  <Shield className="w-5 h-5" /> Digital Membership — payment pending
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-300 mb-2">
                  Your Digital Membership invoice <span className="font-mono text-amber-200">{dmStatus.in_flight?.payment_reference}</span> (${Number(dmStatus.in_flight?.amount || 0).toFixed(2)}) is awaiting payment or verification.
                </p>
                <Link to={`/academy/paynow-payment?reference=${dmStatus.in_flight?.payment_reference}`}>
                  <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10" data-testid="dm-resume-btn">
                    Complete payment →
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Training photos shared by coaches */}
          <SessionPhotosCard />
          <ParentFixturesCard />
          <ParentAvailabilityCard />

          {/* Second parent / guardian contact */}
          <SecondParentCard />

          {/* Credit balance (shown only if parent has a positive balance from a prior overpayment) */}
          {paymentCredits && paymentCredits.balance > 0 && (
            <Card className="border-emerald-500/40 bg-emerald-500/5" data-testid="parent-credit-balance-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-emerald-300">
                  <DollarSign className="w-5 h-5" /> Credit Balance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline justify-between mb-3">
                  <span className="text-4xl font-bold text-emerald-300" data-testid="credit-balance-amount">
                    ${paymentCredits.balance.toFixed(2)}
                  </span>
                  <span className="text-xs text-slate-400">SGD</span>
                </div>
                <p className="text-xs text-slate-300 mb-3">
                  This will be automatically applied against your next academy invoice. No action needed.
                </p>
                {paymentCredits.ledger?.length > 0 && (
                  <details className="text-xs text-slate-400">
                    <summary className="cursor-pointer hover:text-emerald-300" data-testid="credit-history-toggle">
                      View history ({paymentCredits.ledger.length} {paymentCredits.ledger.length === 1 ? "posting" : "postings"})
                    </summary>
                    <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                      {paymentCredits.ledger.map((r, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-slate-800/40 rounded text-slate-300">
                          <div>
                            <p className="text-slate-200">{r.note || r.source || "Credit"}</p>
                            <p className="text-[10px] text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</p>
                          </div>
                          <span className={`font-mono ${r.amount > 0 ? "text-emerald-300" : "text-amber-300"}`}>
                            {r.amount > 0 ? "+" : ""}${Number(r.amount).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </CardContent>
            </Card>
          )}

          {/* Upcoming Sessions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" /> Weekly Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcoming_sessions?.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No scheduled sessions</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {upcoming_sessions?.map((session, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-[#0a0f1a] rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-sm">{session.session_name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                          <span className="font-medium text-purple-400">
                            {session.child_name}
                          </span>
                          <span>•</span>
                          <span>{session.day_of_week}</span>
                          <span>•</span>
                          <span>
                            {session.start_time} - {session.end_time}
                          </span>
                        </div>
                      </div>
                      {session.location && (
                        <div className="flex items-center text-xs text-slate-400">
                          <MapPin className="w-3 h-3 mr-1" />
                          {session.location}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Feedback */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" /> Recent Coach Feedback
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recent_feedback?.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No feedback yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recent_feedback?.slice(0, 5).map((fb, idx) => (
                    <div key={idx} className="p-3 bg-blue-500/10 rounded-lg border-l-4 border-blue-500/30">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm text-blue-400">
                          {fb.child_name}
                        </span>
                        <span className="text-xs text-slate-400">
                          {new Date(fb.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300">{fb.feedback}</p>
                      <p className="text-xs text-slate-400 mt-1">— {fb.coach_name}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" /> Notifications
                  {notifications?.unread_count > 0 && (
                    <Badge className="bg-red-500/100 text-white">
                      {notifications.unread_count}
                    </Badge>
                  )}
                </CardTitle>
                {notifications?.unread_count > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllNotificationsRead}
                    className="text-xs"
                  >
                    Mark all read
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {notifications?.items?.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No notifications</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {notifications?.items?.slice(0, 5).map((notif, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        notif.read
                          ? "bg-[#0a0f1a]"
                          : "bg-yellow-500/10 border-l-4 border-yellow-500/30"
                      }`}
                      onClick={() => !notif.read && markNotificationRead(notif.id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{notif.title}</span>
                        <span className="text-xs text-slate-400">
                          {new Date(notif.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400 line-clamp-2">{notif.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pending Payments Alert */}
        {pending_payments?.total_due > 0 && (
          <Card className="mt-6 border-white/5 bg-yellow-500/10">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-yellow-400">
                <AlertTriangle className="w-5 h-5" /> Pending Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-400">
                    You have outstanding payments totaling{" "}
                    <span className="font-bold text-lg">
                      ${pending_payments.total_due.toFixed(2)}
                    </span>
                  </p>
                  <p className="text-sm text-yellow-500 mt-1">
                    {pending_payments.adhoc_bookings?.length || 0} session booking(s),{" "}
                    {pending_payments.outstanding_payments?.length || 0} outstanding payment(s)
                  </p>
                </div>
                <Link to="/academy/session-upgrade">
                  <Button className="bg-yellow-600 hover:bg-yellow-700">
                    View Details <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Term Info */}
        {term && (
          <Card className="mt-6 bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Current Term</p>
                  <p className="font-bold text-lg text-indigo-400">{term.name}</p>
                  <p className="text-sm text-slate-400">
                    {term.start_date?.split("T")[0]} to {term.end_date?.split("T")[0]} •{" "}
                    {term.duration_weeks} weeks
                  </p>
                </div>
                <Link to="/academy/session-upgrade">
                  <Button variant="outline" className="border-indigo-500/30 text-indigo-400">
                    Add More Sessions
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Credit History Modal */}
      <Dialog open={showCreditHistory} onOpenChange={setShowCreditHistory}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-emerald-400" />
              Credit History: {selectedChildForCredits?.name}
            </DialogTitle>
          </DialogHeader>

          {loadingCredits ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
            </div>
          ) : creditHistory && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-emerald-500/10 rounded-lg text-center">
                  <p className="text-xl font-bold text-emerald-400">{creditHistory.session_credits?.balance || 0}</p>
                  <p className="text-xs text-slate-400">Session Credits</p>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-lg text-center">
                  <p className="text-xl font-bold text-blue-400">${(creditHistory.monetary_credits?.balance || 0).toFixed(2)}</p>
                  <p className="text-xs text-slate-400">Monetary Credits</p>
                </div>
                <div className="p-3 bg-amber-500/10 rounded-lg text-center">
                  <p className="text-xl font-bold text-amber-400">{creditHistory.mc_allowance?.remaining || 0}/{creditHistory.mc_allowance?.limit || 0}</p>
                  <p className="text-xs text-slate-400">MCs Left</p>
                </div>
              </div>

              {/* Upload MC Button */}
              <Button
                onClick={() => {
                  setShowCreditHistory(false);
                  openMCUpload(selectedChildForCredits);
                }}
                className="w-full bg-amber-600 hover:bg-amber-500/100"
                disabled={(creditHistory.mc_allowance?.remaining || 0) === 0}
              >
                <Upload className="w-4 h-4 mr-2" />
                Submit Medical Certificate
              </Button>

              {/* History */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-2">Transaction History</h4>
                {creditHistory.credit_history?.length === 0 ? (
                  <p className="text-slate-400 text-center py-4">No credit history yet</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {creditHistory.credit_history?.map((record) => (
                      <div
                        key={record.id}
                        className={`p-3 rounded-lg border ${
                          record.type === "grant"
                            ? "bg-emerald-500/10 border-emerald-500/20"
                            : "bg-amber-500/10 border-amber-500/20"
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-white flex items-center gap-2">
                              {record.type === "grant" ? (
                                <Plus className="w-4 h-4 text-emerald-400" />
                              ) : (
                                <Clock className="w-4 h-4 text-amber-400" />
                              )}
                              {record.credit_type === "monetary"
                                ? `${record.type === "grant" ? "+" : "-"}$${(record.amount || 0).toFixed(2)}`
                                : `${record.type === "grant" ? "+" : "-"}${record.credits} Credit`}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              {record.reason === "mc_approved" ? "Medical Certificate" : 
                               record.reason === "session_cancelled" ? "Session Cancelled" :
                               record.reason === "rate_change" ? "Rate Adjustment" : 
                               record.reason || "Credit"}
                            </p>
                          </div>
                          <span className="text-xs text-slate-400">
                            {new Date(record.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* MC History */}
              {creditHistory.mc_history?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-2">MC Submissions</h4>
                  <div className="space-y-2">
                    {creditHistory.mc_history?.map((mc) => (
                      <div key={mc.id} className="p-2 bg-slate-800/50 rounded-lg flex justify-between items-center">
                        <div>
                          <p className="text-sm text-white">{mc.mc_date}</p>
                          <p className="text-xs text-slate-400">{mc.term_name}</p>
                        </div>
                        <Badge className={
                          mc.status === "approved" ? "bg-emerald-500/20 text-emerald-400" :
                          mc.status === "rejected" ? "bg-red-500/20 text-red-400" :
                          "bg-amber-500/20 text-amber-400"
                        }>
                          {mc.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* MC Upload Modal */}
      <Dialog open={showMCUpload} onOpenChange={setShowMCUpload}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-400" />
              Upload Medical Certificate
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Submit a medical certificate for <strong className="text-white">{selectedChildForMC?.name}</strong> to receive a session credit.
            </p>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Term *</label>
              <select
                value={selectedTerm}
                onChange={(e) => setSelectedTerm(e.target.value)}
                className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              >
                {terms.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Date of Absence *</label>
              <Input
                type="date"
                value={mcDate}
                onChange={(e) => setMcDate(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Reason (Optional)</label>
              <Input
                placeholder="e.g., Fever, injury..."
                value={mcReason}
                onChange={(e) => setMcReason(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">MC Document *</label>
              <div className="border-2 border-dashed border-slate-700 rounded-lg p-4 text-center">
                {mcFile ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">{mcFile.name}</span>
                    <Button size="sm" variant="ghost" onClick={() => setMcFile(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setMcFile(e.target.files[0])}
                      className="hidden"
                      id="mc-file-input"
                    />
                    <label htmlFor="mc-file-input" className="cursor-pointer">
                      <FileText className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-400">Click to upload image or PDF</p>
                    </label>
                  </>
                )}
              </div>
            </div>

            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-xs text-amber-400">
                <strong>Note:</strong> MC allowance is 1 per 5 sessions enrolled. Your MC will be reviewed by admin before credit is granted.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={uploadMC}
                disabled={uploadingMC || !mcFile || !mcDate}
                className="flex-1 bg-amber-600 hover:bg-amber-500/100"
              >
                {uploadingMC ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Submit MC
              </Button>
              <Button variant="outline" onClick={() => setShowMCUpload(false)} className="border-slate-600 text-slate-300">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Membership Card Upload Modal */}
      <Dialog open={showMembershipUpload} onOpenChange={setShowMembershipUpload}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-400" />
              Upload SCC Membership Card
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Upload a clear photo of your Singapore Cricket Club membership card to verify your membership status.
            </p>

            {membershipCardStatus?.has_card && (
              <div className={`p-3 rounded-lg border ${
                membershipCardStatus.status === "verified" 
                  ? "bg-emerald-500/10 border-emerald-500/30" 
                  : membershipCardStatus.status === "pending"
                    ? "bg-amber-500/10 border-amber-500/30"
                    : "bg-red-500/10 border-red-500/30"
              }`}>
                <p className="text-sm">
                  Current Status: {" "}
                  <span className={
                    membershipCardStatus.status === "verified" 
                      ? "text-emerald-400 font-medium" 
                      : membershipCardStatus.status === "pending"
                        ? "text-amber-400 font-medium"
                        : "text-red-400 font-medium"
                  }>
                    {membershipCardStatus.status === "verified" ? "✓ Verified" : 
                     membershipCardStatus.status === "pending" ? "⏳ Pending Review" : 
                     "Needs Resubmission"}
                  </span>
                </p>
                {membershipCardStatus.submitted_at && (
                  <p className="text-xs text-slate-400 mt-1">
                    Last submitted: {new Date(membershipCardStatus.submitted_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Membership Card Photo *</label>
              <div className="border-2 border-dashed border-slate-700 rounded-lg p-4 text-center">
                {membershipFile ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">{membershipFile.name}</span>
                    <Button size="sm" variant="ghost" onClick={() => setMembershipFile(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <input
                      type="file"
                      id="membershipCardFile"
                      accept="image/*,.pdf"
                      onChange={(e) => setMembershipFile(e.target.files?.[0] || null)}
                      className="hidden"
                    />
                    <label htmlFor="membershipCardFile" className="cursor-pointer">
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">Click to select photo</p>
                      <p className="text-xs text-slate-400 mt-1">JPG, PNG or PDF</p>
                    </label>
                  </>
                )}
              </div>
            </div>

            <div className="bg-slate-800/50 p-3 rounded-lg">
              <p className="text-xs text-slate-400">
                <strong className="text-slate-300">Note:</strong> Member discounts will only be applied after verification. 
                If membership cannot be verified, standard non-member pricing will apply.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={uploadMembershipCard}
                disabled={!membershipFile || uploadingMembership}
                className="flex-1 bg-amber-600 hover:bg-amber-500/100"
              >
                {uploadingMembership ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Upload Card
              </Button>
              <Button variant="outline" onClick={() => setShowMembershipUpload(false)} className="border-slate-600 text-slate-300">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ad-hoc Booking Modal */}
      <Dialog open={showAdhocBooking} onOpenChange={setShowAdhocBooking}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              Book Ad-hoc Session
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Child info */}
            {selectedChildForAdhoc && (
              <div className="p-3 bg-slate-800 rounded-lg">
                <p className="font-medium">{selectedChildForAdhoc.name}</p>
                <p className="text-xs text-slate-400">{selectedChildForAdhoc.age_group || "Unassigned"}</p>
              </div>
            )}

            {/* Pricing Info */}
            {adhocPricing && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
                <p className="text-blue-300 font-medium mb-1">Your Ad-hoc Rate:</p>
                <p className="text-blue-200">
                  {adhocPricing.user_status?.has_term_pass 
                    ? "✅ Term Pass Holder - No premium applied"
                    : adhocPricing.user_status?.is_scc_member
                      ? `SCC Member - Premium rate applies`
                      : `Non-Member - Premium rate applies`
                  }
                </p>
              </div>
            )}

            {/* Session + Date Selection */}
            <div>
              <label className="block text-sm font-medium mb-1">Select session dates</label>
              <p className="text-xs text-slate-400 mb-3">Tap the dates you want. You can pick multiple sessions across the week and pay once.</p>
              {loadingAdhoc ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                </div>
              ) : adhocSessions.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No sessions available</p>
              ) : (
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {adhocSessions.map((session) => {
                    const dayNames = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                    const isDowngrade = session.tier_downgrade_applied;
                    const isBlocked = !!session.unavailable_reason;
                    const dates = session.available_dates || [];
                    return (
                      <div
                        key={session.id}
                        data-testid={`adhoc-session-${session.id}`}
                        className={`p-3 rounded-lg border ${isBlocked ? 'bg-slate-900 border-slate-800 opacity-60' : 'bg-slate-800 border-slate-700'}`}
                      >
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium flex items-center gap-2 flex-wrap">
                              <span>{dayNames[session.day_of_week]} {session.start_time} - {session.end_time}</span>
                              {session.programme_type && (
                                <span
                                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${
                                    isDowngrade
                                      ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40'
                                      : isBlocked
                                        ? 'bg-red-500/15 text-red-300 border border-red-500/40'
                                        : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'
                                  }`}
                                  data-testid={`adhoc-tier-pill-${session.id}`}
                                >
                                  {session.programme_type}
                                  {isDowngrade ? ' • walk-in rate' : isBlocked ? ' • above your tier' : ' • in your tier'}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-slate-400">
                              {session.location} • {session.age_group_name || 'All ages'}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className={`font-bold ${isDowngrade ? 'text-amber-400' : isBlocked ? 'text-red-400' : 'text-orange-400'}`}>
                              ${session.your_adhoc_price?.toFixed(2)}
                            </p>
                            <p className="text-[10px] text-slate-400">per session</p>
                          </div>
                        </div>
                        {isBlocked ? (
                          <p className="text-xs text-red-300">{session.unavailable_reason}</p>
                        ) : dates.length === 0 ? (
                          <p className="text-xs text-slate-500">No upcoming dates in this term</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {dates.map((d) => {
                              const key = `${session.id}__${d}`;
                              const selected = adhocCart.some((i) => i.key === key);
                              const label = new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                              return (
                                <button
                                  key={key}
                                  onClick={() => toggleAdhocDate(session, d)}
                                  data-testid={`adhoc-date-${session.id}-${d}`}
                                  className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                                    selected
                                      ? 'bg-orange-500 border-orange-500 text-white font-semibold'
                                      : 'bg-slate-900 border-slate-600 text-slate-300 hover:border-orange-500/60'
                                  }`}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Cart Summary */}
            {adhocCart.length > 0 && (
              <div className="p-3 rounded-lg border bg-green-500/10 border-green-500/30" data-testid="adhoc-booking-summary">
                <p className="text-sm font-medium text-green-300 mb-2">Selected sessions ({adhocCart.length}):</p>
                <div className="space-y-1 mb-2 max-h-28 overflow-y-auto">
                  {adhocCart.map((i) => {
                    const dayNames = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                    return (
                      <div key={i.key} className="flex items-center justify-between text-sm text-green-200">
                        <span>
                          {dayNames[i.day_of_week]} {new Date(i.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} · {i.start_time}
                        </span>
                        <span className="flex items-center gap-2">
                          <span>${i.price?.toFixed(2)}</span>
                          <button
                            onClick={() => toggleAdhocDate({ id: i.session_id }, i.date)}
                            data-testid={`adhoc-remove-${i.key}`}
                            className="text-slate-400 hover:text-red-400"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between border-t border-green-500/20 pt-2">
                  <span className="text-sm font-medium text-green-300">Total</span>
                  <span className="text-lg font-bold text-green-400" data-testid="adhoc-cart-total">${adhocCartTotal.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={bookAdhocSessions}
                disabled={bookingAdhoc || adhocCart.length === 0}
                className="flex-1 bg-orange-600 hover:bg-orange-500/100"
                data-testid="adhoc-book-pay-btn"
              >
                {bookingAdhoc ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Book &amp; Pay{adhocCart.length > 0 ? ` ($${adhocCartTotal.toFixed(2)})` : ''}
              </Button>
              <Button variant="outline" onClick={() => setShowAdhocBooking(false)} className="border-slate-600 text-slate-300">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
